import 'twin.macro'

import styled from '@emotion/styled'
import { ReactNode, useId, useMemo } from 'react'
import { IMaskInput } from 'react-imask'
import Slider from 'react-input-slider'
import tw, { type TwStyle } from 'twin.macro'
import { createNumberWithUnitMask } from 'utils/i-masks'

import Typography from '../Typography'

type SliderInputProps = {
  value?: string
  step?: number
  max: number
  min: number
  isInputDisabled?: boolean
  isSliderDisabled?: boolean
  disabled?: boolean
  twStyle?: TwStyle
  unit?: string
  thousandsSeparator?: string
  onChange: (value: string) => void
  renderMinText?: (min: number) => ReactNode
  renderMaxText?: (max: number) => ReactNode
}

const sliderInputStyles = {
  track: {
    backgroundColor: '#E8EAEB',
    width: '100%',
    marginInline: 'auto',
    height: '4px',
    display: 'block',
    borderRadius: '0 0 14px 14px'
  },
  active: {
    backgroundColor: '#4F9D3A',
    borderRadius: '0 0 14px 14px'
  },
  thumb: {
    backgroundColor: '#4F9D3A',
    width: '14px',
    height: '14px',
    borderRadius: '3px'
  }
}

const StyledInputMask = styled(IMaskInput)(() => [
  tw`w-full text-sm leading-[18px] tracking-[-0.15px] font-bold min-h-[48px] max-h-[48px] outline-none
  px-5 py-[15px] rounded-t-[14px] border border-transparent focus-within:border-active transition-colors
  bg-input placeholder:font-normal dark:bg-black-20 disabled:cursor-not-allowed`
])

const SliderInput = ({
  step = 1,
  max,
  value,
  min,
  twStyle,
  isInputDisabled,
  isSliderDisabled,
  disabled,
  unit,
  thousandsSeparator,
  onChange,
  renderMaxText,
  renderMinText
}: SliderInputProps) => {
  const inputId = useId()
  const mask = useMemo(
    () => createNumberWithUnitMask({ min, max, unit, thousandsSeparator }),
    [min, max, unit, thousandsSeparator]
  )

  const handleAccept = (val: string) => {
    onChange(val)
  }
  const handleSliderChange = ({ x }: { x: number }) => {
    onChange('' + x)
  }

  return (
    <div css={[twStyle]}>
      <StyledInputMask
        id={inputId}
        mask={mask}
        value={value}
        unmask
        lazy={false}
        disabled={disabled || isInputDisabled}
        onAccept={handleAccept}
      />
      <Slider
        className='slider-input bvi-no-styles'
        axis='x'
        xmin={min}
        xmax={max}
        xstep={step}
        x={Number(value)}
        disabled={disabled || isSliderDisabled}
        styles={sliderInputStyles}
        onChange={handleSliderChange}
      />
      <div tw='flex items-center mt-1'>
        {renderMinText && (
          <Typography variant='caption' twStyle={tw`text-grey-dark`}>
            {renderMinText(min)}
          </Typography>
        )}
        {renderMaxText && (
          <Typography variant='caption' twStyle={tw`ml-auto text-grey-dark`}>
            {renderMaxText(max)}
          </Typography>
        )}
      </div>
    </div>
  )
}

export default SliderInput
